import { useState } from 'react'
import { supabase } from '../supabase'
import { useNavigate, Link } from 'react-router-dom'
import { useBrand } from '../hooks/useBrand'
import CustomerAuthHero from '../components/CustomerAuthHero'

// ── Brand tokens — standalone public page, no CSS vars ──
const NAVY  = '#0B1F3A'
const TEAL  = '#2DD4B0'
const MUTED = '#6B7280'
const BORDER= '#E5E7EB'
const BG    = '#F8FAFC'

const inp = {
  width: '100%', border: '1.5px solid ' + BORDER, borderRadius: '10px',
  padding: '11px 14px', fontSize: '14px', outline: 'none',
  fontFamily: 'Space Grotesk, -apple-system, sans-serif',
  boxSizing: 'border-box', color: '#111827', background: '#fff',
  transition: 'border-color 0.15s', WebkitAppearance: 'none',
}
const iF = e => { e.target.style.borderColor = TEAL }
const iB = e => { e.target.style.borderColor = BORDER }

function Lbl({ children }) {
  return <label style={{ fontSize: '12px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '6px' }}>{children}</label>
}

function ErrBox({ msg }) {
  if (!msg) return null
  return (
    <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', padding: '10px 12px', marginBottom: '14px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: '1px' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      <span style={{ fontSize: '12.5px', color: '#B91C1C', lineHeight: 1.5 }}>{msg}</span>
    </div>
  )
}

function PwInput({ value, onChange, placeholder, onKeyDown }) {
  const [show, setShow] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <input type={show ? 'text' : 'password'} value={value} onChange={onChange}
        placeholder={placeholder || '••••••••'} onKeyDown={onKeyDown}
        style={{ ...inp, paddingRight: '44px' }} onFocus={iF} onBlur={iB} />
      <button type="button" onClick={() => setShow(s => !s)}
        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: MUTED, fontSize: '11.5px', fontWeight: 600, padding: '2px' }}>
        {show ? 'Hide' : 'Show'}
      </button>
    </div>
  )
}

export default function CustomerSignup() {
  const navigate  = useNavigate()
  const { brand } = useBrand()

  const [step, setStep]     = useState(1)
  const [form, setForm]     = useState({ name:'', email:'', phone:'', password:'', confirmPassword:'' })
  const [error, setError]   = useState('')
  const [loading, setLoading] = useState(false)
  const [otpCode, setOtpCode]             = useState('')
  const [otpLoading, setOtpLoading]       = useState(false)
  const [otpError, setOtpError]           = useState('')
  const [resending, setResending]         = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const startCooldown = () => {
    setResendCooldown(60)
    const iv = setInterval(() => setResendCooldown(p => { if (p <= 1) { clearInterval(iv); return 0 } return p - 1 }), 1000)
  }

  const handleSignup = async () => {
    if (!form.name || !form.email || !form.password || !form.phone) { setError('Please fill in all required fields'); return }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return }
    setLoading(true); setError('')

    const { data, error: signUpError } = await supabase.auth.signUp({
      email: form.email, password: form.password,
      options: { data: { role: 'customer' } }
    })
    if (signUpError) { setError(signUpError.message); setLoading(false); return }

    const { error: insertError } = await supabase.from('customer_accounts').insert({
      user_id: data.user.id, name: form.name, email: form.email, phone: form.phone,
    })
    if (insertError) { setError(insertError.message); setLoading(false); return }

    setLoading(false)
    if (data?.session) { navigate('/customer/portal') } else { setStep(2); startCooldown() }
  }

  const handleVerifyOtp = async () => {
    if (otpCode.length < 6) { setOtpError('Please enter the 6-digit code'); return }
    setOtpLoading(true); setOtpError('')
    const { error } = await supabase.auth.verifyOtp({ email: form.email, token: otpCode.trim(), type: 'signup' })
    if (error) { setOtpError(error.message || 'Invalid code — check your email and try again'); setOtpLoading(false); return }
    navigate('/customer/portal')
  }

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return
    setResending(true)
    await supabase.auth.resend({ type: 'signup', email: form.email })
    setResending(false); startCooldown()
  }

  // ── OTP STEP ──
  if (step === 2) {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: 'Space Grotesk, -apple-system, sans-serif' }}>
        <div style={{ background: '#fff', borderRadius: '20px', padding: '40px 32px', width: '100%', maxWidth: '420px', boxShadow: '0 8px 40px rgba(0,0,0,0.08)', textAlign: 'center', border: '1px solid ' + BORDER }}>

          {/* Icon */}
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(45,212,176,0.1)', border: '2px solid rgba(45,212,176,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          </div>

          <div style={{ fontSize: '20px', fontWeight: 800, color: '#111827', marginBottom: '8px' }}>Check your email</div>
          <div style={{ fontSize: '13px', color: MUTED, marginBottom: '4px' }}>We sent a 6-digit code to</div>
          <div style={{ fontSize: '14px', fontWeight: 700, color: NAVY, marginBottom: '24px' }}>{form.email}</div>

          <ErrBox msg={otpError} />

          <div style={{ marginBottom: '16px', textAlign: 'left' }}>
            <Lbl>6-digit confirmation code</Lbl>
            <input type="text" value={otpCode}
              onChange={e => setOtpCode(e.target.value.replace(/\D/g,'').slice(0,6))}
              onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
              placeholder="000000" maxLength={6} autoFocus
              style={{ ...inp, textAlign: 'center', fontSize: '28px', fontWeight: 700, letterSpacing: '0.3em', fontFamily: 'JetBrains Mono, monospace', padding: '16px' }}
              onFocus={iF} onBlur={iB} />
          </div>

          {/* PIN dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
            {[0,1,2,3,4,5].map(i => (
              <div key={i} style={{ width: '10px', height: '10px', borderRadius: '50%', background: otpCode.length > i ? TEAL : BORDER, transition: 'background 0.15s' }} />
            ))}
          </div>

          <button onClick={handleVerifyOtp} disabled={otpLoading || otpCode.length < 6}
            style={{ width: '100%', padding: '13px', borderRadius: '12px', background: otpCode.length === 6 && !otpLoading ? TEAL : BORDER, border: 'none', fontSize: '14px', fontWeight: 700, color: otpCode.length === 6 ? NAVY : '#9CA3AF', cursor: otpCode.length === 6 && !otpLoading ? 'pointer' : 'not-allowed', marginBottom: '16px', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background 0.15s' }}>
            {otpLoading && <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(11,31,58,0.3)', borderTopColor: NAVY, animation: 'ca-spin 0.7s linear infinite' }} />}
            {otpLoading ? 'Verifying...' : 'Confirm email →'}
          </button>

          <div style={{ fontSize: '12px', color: MUTED, marginBottom: '6px' }}>Didn't receive the code?</div>
          <button onClick={handleResendOtp} disabled={resendCooldown > 0 || resending}
            style={{ fontSize: '13px', fontWeight: 600, color: resendCooldown > 0 ? MUTED : NAVY, background: 'none', border: 'none', cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer', fontFamily: 'inherit', marginBottom: '20px' }}>
            {resending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
          </button>

          <div style={{ fontSize: '12px', color: MUTED, padding: '10px 12px', background: BG, borderRadius: '10px', textAlign: 'left', marginBottom: '12px', lineHeight: 1.6 }}>
            Check your spam folder if you don't see it. Code expires in 1 hour.
          </div>
          <button onClick={() => setStep(1)} style={{ fontSize: '12.5px', color: MUTED, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>← Back</button>
        </div>
        <style>{`@keyframes ca-spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    )
  }

  // ── SIGNUP FORM ──
  return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', fontFamily: 'Space Grotesk, -apple-system, sans-serif' }}>

      {/* Hero — 55% */}
      <div className="ca-hero" style={{ flex: '0 0 55%', display: 'none' }}>
        <CustomerAuthHero brand={brand} subtitle="Track your orders, anytime" />
      </div>

      {/* Form — flex 1 */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 32px', overflowY: 'auto', minHeight: '100vh' }}>
        <div style={{ width: '100%', maxWidth: '420px', paddingBottom: '40px' }}>

          {/* Mobile brand */}
          <div className="ca-mobile-brand" style={{ display: 'none', textAlign: 'center', marginBottom: '24px' }}>
            {brand.logo_url
              ? <img src={brand.logo_url} alt={brand.name} style={{ height: '30px', objectFit: 'contain', margin: '0 auto', display: 'block' }} />
              : <div style={{ fontSize: '20px', fontWeight: 800, color: NAVY }}>{brand.name || 'PageConnect'}</div>
            }
          </div>

          <div style={{ fontSize: '22px', fontWeight: 800, color: '#111827', marginBottom: '4px' }}>Create your account</div>
          <div style={{ fontSize: '13px', color: MUTED, marginBottom: '24px' }}>Track orders, download receipts — free forever</div>

          <ErrBox msg={error} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

            <div>
              <Lbl>Full name *</Lbl>
              <input type="text" value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="Your full name" style={inp} onFocus={iF} onBlur={iB} />
            </div>

            <div>
              <Lbl>Phone number * <span style={{ fontWeight: 400, color: '#9CA3AF' }}>— used to find your orders</span></Lbl>
              <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)}
                placeholder="01XXXXXXXXX or +61412345678" style={inp} onFocus={iF} onBlur={iB} />
              <div style={{ fontSize: '12px', color: TEAL, marginTop: '5px', fontWeight: 500 }}>
                Enter the same number you give sellers when ordering
              </div>
            </div>

            <div>
              <Lbl>Email address *</Lbl>
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                placeholder="your@email.com" style={inp} onFocus={iF} onBlur={iB} />
            </div>

            <div>
              <Lbl>Password * <span style={{ fontWeight: 400, color: '#9CA3AF' }}>min 6 characters</span></Lbl>
              <PwInput value={form.password} onChange={e => set('password', e.target.value)} />
            </div>

            <div>
              <Lbl>Confirm password *</Lbl>
              <PwInput value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSignup()} />
            </div>

            {/* Benefits */}
            <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '12px', padding: '14px 16px' }}>
              <div style={{ fontSize: '12.5px', fontWeight: 700, color: '#15803D', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#15803D" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                Free forever — no credit card needed
              </div>
              {['Track all your orders across all sellers','Download receipts as PDF','View your full spending history','Message sellers directly'].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: i < 3 ? '6px' : 0 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                  <span style={{ fontSize: '12px', color: '#166534' }}>{item}</span>
                </div>
              ))}
            </div>

            <button onClick={handleSignup} disabled={loading}
              style={{ width: '100%', padding: '14px', borderRadius: '12px', background: loading ? BORDER : NAVY, border: 'none', fontSize: '14px', fontWeight: 700, color: loading ? '#9CA3AF' : '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'opacity 0.15s' }}>
              {loading && <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'ca-spin 0.7s linear infinite' }} />}
              {loading ? 'Creating account...' : 'Create free account'}
            </button>

            <div style={{ textAlign: 'center', fontSize: '13px', color: MUTED }}>
              Already have an account?{' '}
              <Link to="/customer/login" style={{ fontWeight: 600, color: NAVY, textDecoration: 'none' }}>Sign in</Link>
            </div>
            <div style={{ textAlign: 'center', fontSize: '12px', color: MUTED }}>
              Seller?{' '}
              <Link to="/signup" style={{ fontWeight: 600, color: MUTED, textDecoration: 'none', borderBottom: '1px solid ' + BORDER }}>Create seller account</Link>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ca-spin { to { transform: rotate(360deg) } }
        .ca-hero { display: flex !important; }
        @media(max-width: 768px) { .ca-hero { display: none !important; } .ca-mobile-brand { display: block !important; } }
      `}</style>
    </div>
  )
}